
import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Copy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from '@/hooks/use-toast';
import { useAuthSession } from '@/hooks/use-auth-session';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useNavigate, useParams } from 'react-router-dom';

interface PlaceHeaderProps {
  isEditing: boolean;
  isOwner: boolean;
  name: string;
  onNameChange: (name: string) => void;
  onBack: () => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
}

export function PlaceHeader({
  isEditing,
  isOwner,
  name,
  onNameChange,
  onBack,
  onEdit,
  onSave,
  onCancel,
}: PlaceHeaderProps) {
  const [isSaveDialogOpen, setIsSaveDialogOpen] = React.useState(false);
  const [isLoginDialogOpen, setIsLoginDialogOpen] = React.useState(false);
  const { toast } = useToast();
  const session = useAuthSession();
  const navigate = useNavigate();
  const { id: placeId } = useParams();

  const { data: collections } = useQuery({
    queryKey: ['user-collections'],
    queryFn: async () => {
      if (!session?.user?.id) return [];
      
      const { data, error } = await supabase
        .from('destination_playlists')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!session?.user?.id
  });

  const handleSaveToCollection = async (collectionId?: string) => {
    try {
      if (!session?.user?.id) {
        setIsLoginDialogOpen(true);
        return;
      }

      if (!placeId) {
        throw new Error('Place ID is missing');
      }

      const { data, error } = await supabase.rpc('copy_place_to_collection', {
        place_id: placeId,
        target_playlist_id: collectionId || null,
        user_id: session.user.id
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Place saved to collection",
      });

      setIsSaveDialogOpen(false);
    } catch (error: any) {
      console.error('Error saving place:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save place to collection",
        variant: "destructive",
      });
    }
  };

  const handleSaveClick = () => {
    if (!session) {
      setIsLoginDialogOpen(true);
    } else {
      setIsSaveDialogOpen(true);
    }
  };

  return (
    <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:justify-between mb-6">
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        {isEditing ? (
          <input
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            className="text-2xl font-bold bg-transparent border-b border-gray-200 focus:border-primary focus:outline-none px-1"
          />
        ) : (
          <h1 className="text-2xl font-bold">{name}</h1>
        )}
      </div>

      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          onClick={handleSaveClick}
        >
          <Copy className="w-4 h-4 mr-2" />
          Save to Collection
        </Button>
        
        {isOwner && (
          isEditing ? (
            <>
              <Button variant="ghost" onClick={onCancel}>Cancel</Button>
              <Button onClick={onSave}>Save Changes</Button>
            </>
          ) : (
            <Button onClick={onEdit}>Edit</Button>
          )
        )}
      </div>

      <Dialog open={isLoginDialogOpen} onOpenChange={setIsLoginDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Login Required</DialogTitle>
            <DialogDescription>
              You need to be logged in to save places to your collections.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLoginDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              setIsLoginDialogOpen(false);
              navigate('/auth');
            }}>
              Log In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isSaveDialogOpen} onOpenChange={setIsSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save to Collection</DialogTitle>
            <DialogDescription>
              Choose a collection to save this place to, or create a new one automatically.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {collections?.length ? (
              <div className="grid gap-2">
                {collections.map((collection) => (
                  <Button
                    key={collection.id}
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleSaveToCollection(collection.id)}
                  >
                    <span className="truncate">{collection.name}</span>
                    {collection.city && (
                      <span className="ml-2 text-sm text-muted-foreground">
                        ({collection.city})
                      </span>
                    )}
                  </Button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No collections found. A new collection will be created automatically based on the place's location.
              </p>
            )}

            <Button 
              className="w-full"
              onClick={() => handleSaveToCollection(null)}
            >
              Save to New Collection
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
